import PropTypes from 'prop-types';
import React from 'react';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';
import _omit from 'lodash/omit';
import styled, { ThemeProvider } from 'styled-components';

import Pagination from 'ravenjs/lib/Pagination';
import SearchInput from 'ravenjs/lib/SearchInput';
import Table, { TableBody, TableHead } from 'ravenjs/lib/Table_old';
import { THEME } from 'ravenjs/constants/theme';
import { calculateSort } from 'ravenjs/utils/sorting';
import { extractAPIConfig, fetchAPIData } from 'ravenjs/utils/apis';
import { getThemeProps } from 'ravenjs/utils/theme';
import { mergeObjects } from 'ravenjs/utils/merge';
import { warning } from 'ravenjs/utils/warning';

const ListViewStyled = styled.div`
    position: relative;
    /**
     * Add all of the remaining styles from theme
     */
    ${getThemeProps('ListView.styles')};
`;

const ListViewSearch = styled.div`
    position: relative;
    max-width: 300px;
    margin: 15px auto;
    /**
     * Add all of the remaining styles from theme
     */
    ${getThemeProps('ListViewSearch.styles')};
`;

// eslint-disable-next-line valid-jsdoc
/**
 * A list view, also referred to as an HTML table, renders multiple line items vertically as a single continuous element.
 *
 * List views are made up of multiple continuous columns of rows. Each row's item gets mapped to the passed in data via the current `column.id`.
 *
 * List views are best suited for similar data types.
 *
 * @class
 * @extends Component
 */
class ListView extends React.Component {
    static propTypes = {
        /**
         * A base URL for making the API calls.
         */
        baseUrl: PropTypes.string,
        /**
         * A map of cell types.
         */
        cellTypes: PropTypes.object,
        /**
         * The main data for the body of the component.
         */
        data: PropTypes.array,
        /**
         * A map of action handlers used by rendered cells.
         */
        handlers: PropTypes.object,
        /**
         * Text to display while loading the data.
         */
        loadingText: PropTypes.string,
        /**
         * An `oAuth` token to make API calls.
         */
        oAuthToken: PropTypes.string,
        /**
         * Callback fired during a page switch cycle.
         */
        onChangePage: PropTypes.func,
        /**
         * Callback fired during rows per page change cycle.
         */
        onChangeRowsPerPage: PropTypes.func,
        /**
         * Callback fired during the fetch data cycle.
         */
        onLoad: PropTypes.func,
        /**
         * `renderProp` to allow for a table-row to be selectable.
         */
        selectable: PropTypes.func,
        /**
         * `renderProp` to allow for all the table-rows to be select all.
         */
        renderSelectAll: PropTypes.func,
        /**
         * The JSON schema for the `ListView`.
         */
        schema: PropTypes.object.isRequired,
        /**
         * The `context` which gets passed down to the `<Table />`.
         */
        tableContext: PropTypes.object,
        /**
         * The default theme for the `ListView` component.
         */
        theme: PropTypes.object,
    };

    static defaultProps = {
        baseUrl: null,
        cellTypes: null,
        data: null,
        handlers: {},
        loadingText: 'loading',
        oAuthToken: null,
        onChangePage: null,
        onChangeRowsPerPage: null,
        onLoad: null,
        selectable: null,
        renderSelectAll: null,
        tableContext: null,
        theme: THEME,
    };

    constructor(props) {
        super(props);

        const { loadingText, schema, onLoad } = this.props;

        this.state = {
            currentPage: 1,
            data: null,
            loadingText,
            order: _get(schema, 'sorting.default.order', ''),
            orderBy: _get(schema, 'sorting.default.orderBy', ''),
            rowsPerPage: _get(schema, 'pagination.rowsPerPage', 5),
            searchSpec: null,
            totalRows: 0,
        };
        typeof onLoad === 'function' && onLoad(true);
    }

    componentDidMount() {
        // Fetch the data upon mounting.
        this.fetchData({ props: this.props, ...this.state });
    }

    componentDidUpdate(prevProps, prevState) {
        const { data, schema, oAuthToken } = this.props;
        const { data: prevData, schema: prevSchema, oAuthToken: prevToken } = prevProps;
        const { currentPage, loadingText, order, orderBy, rowsPerPage, searchSpec } = this.state;
        const filteredPrevState = _omit(prevState, ['data', 'totalRows']);
        const filteredState = _omit(this.state, ['data', 'totalRows']);
        // Verify that the schemas are different
        const schemasDifferent = !_isEqual(prevSchema, schema);
        // Verify that the data is different
        const dataDifferent = !_isEqual(prevData, data);
        // Verify that the oAuthToken changed
        const oAuthTokenDifferent = !_isEqual(oAuthToken, prevToken);
        // Verify if filtered states are different
        const statesDifferent = !_isEqual(filteredPrevState, filteredState);
        // If the data hasn't loaded, or the prev + current schemas are different,
        // let's load the data!
        if (oAuthTokenDifferent || dataDifferent || schemasDifferent || statesDifferent) {
            this.fetchData({
                currentPage: schemasDifferent ? 1 : currentPage,
                loadingText,
                order: schemasDifferent ? _get(schema, 'sorting.default.order', '') : order,
                orderBy: schemasDifferent ? _get(schema, 'sorting.default.orderBy', '') : orderBy,
                props: this.props,
                resetData: schemasDifferent,
                rowsPerPage: schemasDifferent && rowsPerPage,
                searchSpec,
            });
        }
    }

    getCurrentTheme = () => {
        const propsTheme = _get(this.props, 'theme', {});
        const schemaTheme = _get(this.props, 'schema.theme', {});

        const theme = mergeObjects(propsTheme, schemaTheme);

        return theme;
    };

    fetchData = ({
        currentPage,
        loadingText,
        order,
        orderBy,
        props,
        resetData,
        rowsPerPage,
        searchSpec,
    }) => {
        const { baseUrl, data, oAuthToken, onLoad: onLoadProps, schema } = props;
        const { currentPage: currentPageFromState, rowsPerPage: rowsPerPageFromState } = this.state;
        const apis = _get(schema, 'apis');
        const onLoad = typeof onLoadProps === 'function' ? onLoadProps : () => {};

        if (resetData) {
            // Update the state.
            this.setState(prevState => ({
                currentPage: currentPage || prevState.currentPage,
                data: [],
                order,
                orderBy,
                rowsPerPage: rowsPerPage || prevState.rowsPerPage,
                searchSpec: '',
                totalRows: 0,
            }));
        }

        if (_isEmpty(data) && !_isEmpty(apis)) {
            // Throw a warning if there is no `oAuthToken` is not found
            warning(!_isEmpty(oAuthToken), [
                `A <ListView /> requires an oAuthToken to fetch the requested data.`,
            ]);
            // Set the loading to true.
            onLoad(true, loadingText);
            // Extract the API config from the schema.
            const api = extractAPIConfig(apis, 'data');
            // Extract the request object from API config.
            const request = _get(api, 'request', {});
            // Extract the requested `dataKey` for the final fetched data.
            const dataKey = _get(api, 'dataKey', 'data');
            // Extract the requested `totalRows` key for the final fetched data.
            const totalRowsKey = _get(api, 'totalKey', 'totalRecords');
            // Build the `offset` for the paginated data.
            const offset =
                currentPageFromState > 1 ? (currentPageFromState - 1) * rowsPerPageFromState : 0;
            // Calculate the sorting.
            const sort = calculateSort(order, orderBy);
            // Build the axios config
            const config = {
                ...request,
                baseURL: baseUrl || request.baseURL,
                headers: {
                    Authorization: `Bearer ${oAuthToken}`,
                },
                params: {
                    ..._get(request, 'params'),
                    limit: rowsPerPageFromState,
                    offset,
                    sort,
                    searchSpec,
                },
            };

            if (!_isEmpty(oAuthToken) && !_isEmpty(config.baseURL)) {
                // Fetch the data and update the state.
                fetchAPIData(config, { data: dataKey, totalRows: totalRowsKey })
                    .then(resp => {
                        // Total rows returned is a String, we need to parse it into an Integer.
                        const totalRows = resp.totalRows && parseInt(resp.totalRows, 10);
                        // Set the loading to false when we have fetched data.
                        onLoad(false);
                        // Update the state with the fetched data.
                        this.setState(() => ({
                            ...resp,
                            totalRows,
                        }));
                    })
                    .catch(error => {
                        // Set the loading to false when there is an error.
                        onLoad(false);
                        // Update the state with the error.
                        this.setState(() => ({
                            error,
                        }));
                    });
            } else {
                // Set the loading to false when we have a missing `oAuthToken`.
                onLoad(false);
            }
        } else {
            // Set the loading to false when we have updated the data.
            onLoad(false);

            // Update the state
            this.setState(() => ({
                data,
                totalRows: _get(
                    schema,
                    'pagination.totalRows',
                    Array.isArray(data) ? data.length : 0
                ),
            }));
        }
    };

    handlePageChange = (event, page) => {
        const { onChangePage } = this.props;
        // Set the current page on the state
        page && this.setState(() => ({ currentPage: page }));
        // If onChangePage is defined, we fire it with current page
        typeof onChangePage === 'function' && onChangePage(page);
    };

    handleRowsPerPageChange = rowsPerPage => {
        const { onChangeRowsPerPage } = this.props;
        const value = _get(rowsPerPage, 'value');
        // Set the current rows per page on the state
        value && this.setState(() => ({ rowsPerPage: value }));
        // If onChangeRowsPerPage is defined, we fire it with new rows per page
        typeof onChangeRowsPerPage === 'function' && onChangeRowsPerPage(value);
    };

    handleSearchKeyPress = e => {
        // If we are pressing the 'Enter' key,
        // let's update the search spec.
        // (e.key === 'Enter') === (charCode === 13)
        if (e.charCode === 13) {
            e.target.blur();
            this.updateSearchSpec(e);
        }
    };

    handleSearchOnClick = value => {
        // If value is present, let's update the search spec.
        return value && this.updateSearchSpec({ target: { value } });
    };

    handleSearchOnCancel = () => {
        // Nullify the search spec.
        return this.setState(() => ({ searchSpec: null }));
    };

    handleSortChange = (order, orderBy) => () => {
        const newOrder = order && order === 'asc' ? 'desc' : 'asc';
        // Set the current order and orderBy on the state
        newOrder &&
            orderBy &&
            this.setState(prevState => ({
                order: prevState.orderBy === orderBy ? newOrder : 'asc',
                orderBy,
            }));
    };

    updateSearchSpec = event => {
        // Extract the value from the current event
        const value = _get(event, 'target.value');
        // Update the state with the new search spec.
        return value && this.setState(() => ({ searchSpec: value }));
    };

    renderBottomPagination = () => {
        // Extract the pagination position (top, bottom, both ?)
        const position = _get(this.props, 'schema.pagination.position', null);
        // If the position is bottom, then render pagination
        return position === 'bottom' || position === 'both' ? this.renderPagination() : null;
    };

    renderPagination = () => {
        // Extract the pagination schema from the schema
        const paginationSchema = _get(this.props, 'schema.pagination', {});

        if (paginationSchema.hidePagination) {
            return null;
        }
        const { currentPage, rowsPerPage, totalRows } = this.state;
        // Construct the pagination properties
        const paginationProps = {
            currentPage,
            onChangePage: this.handlePageChange,
            onChangeRowsPerPage: this.handleRowsPerPageChange,
            rowsPerPage: rowsPerPage || paginationSchema.rowsPerPage,
            totalRows,
        };
        // If we have a valid element, render it
        return <Pagination {...paginationSchema} {...paginationProps} />;
    };

    renderSearch = () => {
        const { schema } = this.props;
        // Extract the search spec from the schema
        const searchSchema = _get(schema, 'search', null);
        // Extract the placeholder from the `searchSchema`
        const placeholder = _get(searchSchema, 'placeholder', 'Search');
        // Build the search input props
        const searchInputProps = {
            onCancel: this.handleSearchOnCancel,
            onClick: this.handleSearchOnClick,
            onKeyPress: this.handleSearchKeyPress,
            placeholder,
        };
        // If the `searchSchema` is present and not empty, then render the search.
        return (
            !_isEmpty(searchSchema) && (
                <ListViewSearch>
                    <SearchInput {...searchInputProps} type="search" enterKeyHint="go" />
                </ListViewSearch>
            )
        );
    };

    renderTopPagination = () => {
        // Extract the pagination position (top, bottom, both ?)
        const position = _get(this.props, 'schema.pagination.position', null);
        // If the position is top, then render pagination
        return position === 'top' || position === 'both' ? this.renderPagination() : null;
    };

    render() {
        const { data, order, orderBy } = this.state;
        const {
            baseUrl,
            cellTypes,
            handlers,
            oAuthToken,
            schema,
            selectable,
            renderSelectAll,
            tableContext,
        } = this.props;

        if (_isEmpty(schema)) {
            warning(!_isEmpty(schema), [
                `A <ListView /> must be supplied with a valid schema object.`,
                `The current schema is either empty, or doesn't have sufficient config for the list to render.`,
            ]);
            // Can't display the list without a schema
            return null;
        }

        return (
            <ThemeProvider theme={this.getCurrentTheme()}>
                <ListViewStyled>
                    {this.renderSearch()}
                    {this.renderTopPagination()}
                    <Table
                        context={{
                            baseUrl,
                            cellTypes,
                            data,
                            handlers,
                            oAuthToken,
                            order,
                            orderBy,
                            schema,
                            selectable,
                            ...tableContext,
                        }}
                    >
                        <TableHead
                            onSortChange={this.handleSortChange}
                            renderSelectAll={renderSelectAll}
                        />
                        <TableBody />
                    </Table>
                    {this.renderBottomPagination()}
                </ListViewStyled>
            </ThemeProvider>
        );
    }
}

export default ListView;
